import React, { useState } from 'react';
import styled from 'astroturf/react';
import { Map } from 'immutable';

import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'js/core_pre_webpack/routes';
import User from 'js/core_post_webpack/models/user';
import Title from 'js/canonical_cards/work_card/title';
import AuthorList from 'js/canonical_cards/shared/author_list';
import ResearchInterestList from 'js/canonical_cards/work_card/research_interest_list';
import Thumbnail from 'js/videos/shared/Thumbnail';
import { Author } from './canonical_work_results';

import pluralize from 'pluralize';
import { MarketingCourse } from 'js/courses/shared/course_helper';

// This is the same MOBILE_WIDTH as is in Upsell
// They should be the same number
const MOBILE_WIDTH = 750;

interface ResearchInterest {
  id: number;
  name: string;
  url: string;
}
export interface Course extends MarketingCourse {
  owner: Author;
  researchInterests: ResearchInterest[];
}

interface Props {
  courses: Course[];
}

const Container = styled.div`
  grid-column: 1 / 4;
  width: 100%;
`;

const CourseCardWrapper = styled.div`
  background-color: white;
  margin-top: 16px;
  padding: 20px;
  border-style: solid;
  border-width: 1px;
  border-color: #ededed #ededed #e1e1e1;
  border-radius: 9px;
  box-shadow: 0 0 3px 0 rgba(0, 0, 0, 0.05);
  display: flex;

  @media (max-width: ${MOBILE_WIDTH}px) {
    flex-direction: column;
  }
`;

const CourseDetailsWrapper = styled.div`
  flex-grow: 1;
`;

const ThumbnailWrapper = styled.a`
  flex-grow: 0;
  flex-shrink: 0;
  width: 244px;
  height: 179px;
  margin-right: 16px;

  @media (max-width: ${MOBILE_WIDTH}px) {
    width: 100%;
    height: 56.25%;
    margin-bottom: 8px;
    margin-right: 0;
  }

  @media (max-width: 903px) and (min-width: ${MOBILE_WIDTH + 1}px) {
    width: 120px;
    height: 88px;
  }
`;

const ThumbnailContainer = styled.div`
  position: relative;
  flex: none;
  width: 100%;
  height: 100%;
  margin-right: 10px;
  background: #fffff;
  border: 1px solid rgba(0, 0, 0, 0.15);
  box-sizing: content-box;
`;

const CourseLink = styled.a`
  color: inherit !important;
  cursor: pointer;
`;

const StatsOuterWrapper = styled.span`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 12px;
  align-items: center;
  font-size: 12px;
`;

const StatsInnerWrapper = styled.span`
  display: flex;
  margin-right: 12px;
  align-items: center;
  flex: 0 0 auto;
  height: 20px;
`;

const StatIcon = styled.i`
  font-size: 11px;
  margin-right: 4px;
`;

export default function CourseResults({ courses }: Props) {
  // This holds indiviual authors
  // and the data to show for their popovers:
  // views, works, and followers.
  const [actors, setActors] = useState(
    courses.reduce(
      (acc, c) => {
        acc[c.owner.id] = c.owner;
        return acc;
      },
      {} as {
        [id: number]: Pick<Author, 'followerCount' | 'viewCount' | 'workCount'>;
      },
    ),
  );

  // Used to show which users a user is following
  // key: user id
  // value: boolean, true for following, false for not
  // The api doesn't return following values for authors,
  // so we could get away with just using a Set,
  // but we're using a Map for consistency, and it
  // should be easier in the future if the API _does_
  // return whether the current user is following the author
  const [following, setFollowing] = useState(Map<number, boolean>());

  // Used to show which research interests a user is following
  // key: ri id
  // value: boolean, true for following, false for not
  // The api doesn't return following values for ris,
  // so we could get away with just using a Set,
  // but we're using a Map for consistency, and it
  // should be easier in the future if the API _does_
  // return whether the current user is following the ri
  const [followingRis, setFollowingRis] = useState(Map<number, boolean>());

  // This holds state for how many followers a research interest has,
  // used in the research interest popover
  const [ris, setRis] = useState<{ [id: number]: { followerCount: number } }>(
    {},
  );

  // `actor` in this context is a user (the author)
  const fetchActorData = async ({ id }: { id: number }) => {
    // We create a new params object here because the `routes`
    // functions bungle the object if we try to reuse it between
    // calls 😑
    const params = () => ({
      user_id: id,
      subdomain_param: 'api',
    });

    const getFollowers = fetch(routes.api_v0_user_followers_path(params()));
    const getViews = fetch(routes.api_v0_user_stats_path(params()));
    const getWorks = fetch(routes.count_api_v0_user_works_path(params()));

    const responses = await Promise.all([getFollowers, getViews, getWorks]);
    const [followerData, viewData, workData] = await Promise.all(
      responses.map((res) => res.json()),
    );

    setActors((a) => ({
      ...a,
      [id]: {
        followerCount: followerData.meta.count,
        viewCount: viewData.all_time_stats.documents_count,
        workCount: workData.count,
      },
    }));
  };

  const fetchResearchInterestFollowerCount = async ({ id }: { id: number }) => {
    const params = {
      interest_id: id,
      subdomain_param: 'api',
    };

    fetch(routes.count_api_v0_interest_followers_path(params))
      .then((res) => res.json())
      .then((data) => data.count)
      .then((count) =>
        setRis((ris) => ({ ...ris, [id]: { followerCount: count } })),
      );
  };

  const followResearchInterest = ({ id }: { id: number }, cb: () => void) => {
    const params = {
      subdomain_param: 'api',
      'tag[id]': id,
      tag_type: 'ResearchInterest',
      target_type: 'User',
      target_id: User?.current?.id,
    };

    fetchWithCsrf(routes.add_api_v0_taggings_path(params), {
      method: 'POST',
    })
      .then(() => setFollowingRis((r) => r.set(id, true)))
      .then(cb);
  };

  const followUser = (id: string | number, cb: () => void) => {
    const params = {
      user_id: id,
      subdomain_param: 'api',
    };

    fetchWithCsrf(routes.api_v0_user_following_index_path(params))
      .then(() => setFollowing((f) => f.set(Number(id), true)))
      .then(cb);
  };

  const unfollowResearchInterest = ({ id }: { id: number }, cb: () => void) => {
    const params = {
      subdomain_param: 'api',
      'tag[id]': id,
      tag_type: 'ResearchInterest',
      target_type: 'User',
      target_id: User?.current?.id,
    };

    fetchWithCsrf(routes.remove_api_v0_taggings_path(params), {
      method: 'POST',
    })
      .then(() => setFollowingRis((r) => r.remove(id)))
      .then(cb);
  };

  const unfollowUser = (id: string | number, cb: () => void) => {
    const params = {
      user_id: id,
      subdomain_param: 'api',
    };

    fetchWithCsrf(routes.api_v0_user_following_index_path(params), {
      method: 'DELETE',
    })
      .then(() => setFollowing((f) => f.set(Number(id), false)))
      .then(cb);
  };

  return (
    <Container>
      {courses.map((course) => {
        const courseUrl = routes.course_url({ id: course.id });
        return (
          <CourseCardWrapper key={`Course-${course.id}`}>
            <ThumbnailWrapper href={courseUrl}>
              <Thumbnail
                url={course.thumbnailUrl}
                ContainerComponent={ThumbnailContainer}
              />
            </ThumbnailWrapper>
            <CourseDetailsWrapper>
              <CourseLink href={courseUrl}>
                <Title title={course.title} workId={course.id} query={null} />
              </CourseLink>
              <AuthorList
                authors={[
                  {
                    ...course.owner,
                    ...actors[course.owner.id],
                    isFollowing: following.get(course.owner.id),
                  },
                ]}
                enablePopover
                fetchActorData={fetchActorData}
                followUser={followUser}
                unfollowUser={unfollowUser}
                addClickTracking={(url) => url}
              />
              <ul className="fa-ul">
                {course.description.map((point) => (
                  <li key={point}>
                    <i className="fa-li fa fa-check" /> {point}
                  </li>
                ))}
              </ul>
              <ResearchInterestList
                interests={course.researchInterests.map((ri) => ({
                  ...ri,
                  ...ris[ri.id],
                  isFollowing: followingRis.get(ri.id),
                }))}
                fetchResearchInterestFollowerCount={
                  fetchResearchInterestFollowerCount
                }
                followResearchInterest={followResearchInterest}
                unfollowResearchInterest={unfollowResearchInterest}
              />
              <StatsOuterWrapper>
                <StatsInnerWrapper>
                  <StatIcon className="fa fa-video-camera" />
                  {'  '}
                  {course.videoCount} videos
                </StatsInnerWrapper>
                <StatsInnerWrapper>
                  <StatIcon className="fa fa-clock-o" />
                  {course.duration}
                </StatsInnerWrapper>
                <StatsInnerWrapper>
                  <StatIcon className="fa fa-eye" />
                  <span style={{ textTransform: 'lowercase' }}>
                    {`${parseInt(course.views || '0')} ${pluralize('View', parseInt(course.views || '0'))}`}
                  </span>
                </StatsInnerWrapper>
              </StatsOuterWrapper>
            </CourseDetailsWrapper>
          </CourseCardWrapper>
        );
      })}
    </Container>
  );
}
